/**
 * Per-Step Rollup Builder
 *
 * @module workflows/diagnostics/materialize/step-rollup-builder
 * @description Writes one `steps/<NNN>-<stepId>.md` per workflow step. Each rollup is
 * self-contained: metadata, thoughts, in-window network/console events, navigation
 * transitions, assertion outcomes, and links to artifact paths.
 *
 * The agent reads the failed step's rollup *second* (after `summary.md`), then walks
 * backwards through prior rollups. Rollups are bounded by step duration, so they fit
 * in context even for long runs.
 */

import * as fs from 'node:fs/promises';
import { computeStepWorkWindows, formatPrefix, formatRelative, oneLine } from './_shared.js';
import {
  resolveWorkspacePath,
  stepRollupRelativePath,
  thoughtsByStepRelativePath,
  workflowNodeRelativePath,
  WORKSPACE_PATHS,
} from './paths.js';
import type { RunDataStepDiagnostics } from './run-data.js';

const diagnosticRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const diagnosticText = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const diagnosticNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function appendAuthDiagnostics(
  lines: string[],
  diagnostics: NonNullable<RunDataStepDiagnostics['authDiagnostics']>
): void {
  const generation = diagnostics.snapshot.generation;
  lines.push(
    `- auth state: ${diagnostics.isAuthenticated ? 'authenticated' : 'not authenticated'}; snapshot=${diagnostics.snapshot.provenance}${generation === null ? '' : ` generation ${generation}`}`
  );

  const current = diagnostics.browser?.current;
  if (current) {
    const connected = typeof current.connected === 'boolean' ? current.connected : null;
    const url = diagnosticText(current.url);
    const recoveryGeneration = diagnosticNumber(current.recoveryGeneration);
    lines.push(
      `- browser auth context: connected=${connected ?? '?'}${recoveryGeneration === null ? '' : `; recoveryGeneration=${recoveryGeneration}`}${url ? `; url=${url}` : ''}`
    );
  }

  const events = diagnostics.browser?.events ?? [];
  const lastRestore = events
    .slice()
    .reverse()
    .find((event) => event.kind === 'storage_restore');
  if (lastRestore) {
    const phase = diagnosticText(lastRestore.phase) ?? '?';
    const restoredDatabases = diagnosticNumber(lastRestore.restoredDatabases);
    const expectedDatabases = diagnosticNumber(lastRestore.expectedDatabases);
    const restoredStores = diagnosticNumber(lastRestore.restoredStores);
    const expectedStores = diagnosticNumber(lastRestore.expectedStores);
    const restoredEntries = diagnosticNumber(lastRestore.restoredEntries);
    const expectedEntries = diagnosticNumber(lastRestore.expectedEntries);
    const matchedLocalStorageEntries = diagnosticNumber(lastRestore.matchedLocalStorageEntries);
    const expectedLocalStorageEntries = diagnosticNumber(lastRestore.expectedLocalStorageEntries);
    const durationMs = diagnosticNumber(lastRestore.durationMs);
    const error = diagnosticText(lastRestore.error);
    lines.push(
      `- auth storage restore: ${phase}; localStorage=${matchedLocalStorageEntries ?? '?'}/${expectedLocalStorageEntries ?? '?'}; databases=${restoredDatabases ?? '?'}/${expectedDatabases ?? '?'}; stores=${restoredStores ?? '?'}/${expectedStores ?? '?'}; entries=${restoredEntries ?? '?'}/${expectedEntries ?? '?'}${durationMs === null ? '' : `; ${durationMs}ms`}${error ? `; error=${oneLine(error)}` : ''}`
    );
  }

  const lastProvider = events
    .slice()
    .reverse()
    .find((event) => event.kind === 'auth_request');
  if (lastProvider) {
    const evidence = diagnosticRecord(lastProvider.evidence);
    const provider = diagnosticText(evidence?.provider) ?? '?';
    const endpoint = diagnosticText(evidence?.endpoint) ?? '?';
    const phase = diagnosticText(lastProvider.phase) ?? '?';
    const status = diagnosticNumber(lastProvider.status);
    const code = diagnosticText(lastProvider.providerErrorCode);
    const durationMs = diagnosticNumber(lastProvider.durationMs);
    lines.push(
      `- auth provider: ${provider} ${endpoint}; ${phase}${status === null ? '' : ` ${status}`}${code ? ` ${code}` : ''}${durationMs === null ? '' : `; ${durationMs}ms`}`
    );
  }

  const lifecycleEvents = events.filter((event) => event.kind === 'browser_lifecycle');
  if (lifecycleEvents.length > 0) {
    lines.push(
      `- browser lifecycle: ${lifecycleEvents.map((event) => diagnosticText(event.event) ?? '?').join(' → ')}`
    );
  }

  const eventLoop = diagnostics.worker.eventLoop;
  if (eventLoop) {
    const max = diagnosticNumber(eventLoop.delayMaxMs);
    const p95 = diagnosticNumber(eventLoop.delayP95Ms);
    const severity = diagnosticText(eventLoop.severity);
    lines.push(
      `- worker event loop at capture: severity=${severity ?? '?'}; max=${max ?? '?'}ms; p95=${p95 ?? '?'}ms`
    );
  }
}

// ============================================================================
// Inputs (subset of LedgerInput; reuses the same in-memory shape from materialize.ts)
// ============================================================================

export interface StepRollupStep {
  id: string;
  nodeId: string;
  orderIndex: number;
  name: string | null;
  nodeType: string | null;
  status: string;
  startedAtMs: number | null;
  finishedAtMs: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  cacheOutcome: string | null;
  cacheHitSource: string | null;
  diagnostics?: RunDataStepDiagnostics;
}

export interface StepRollupThought {
  stepId: string | null;
  timestampMs: number;
  toolName: string | null;
  content: string;
}

export interface StepRollupAction {
  action: string;
  target: string | null;
  url: string | null;
  timestampMs: number | null;
  success: boolean;
  isAnnotation: boolean;
}

export interface StepRollupNavigation {
  timestampMs: number | null;
  action: string;
  from: string | null;
  to: string | null;
  crossHost: boolean;
}

export interface StepRollupNetwork {
  timestampMs: number | null;
  url: string;
  method: string;
  status: number | null;
}

export interface StepRollupConsole {
  timestampMs: number | null;
  type: string;
  text: string;
}

export interface StepRollupAssertion {
  stepId: string;
  timestampMs: number | null;
  condition: string | null;
  severity: string | null;
  nonBlocking: boolean;
  passed: boolean;
  reason: string | null;
}

/** A kept screencast frame, projected onto the run-start axis. */
export interface StepRollupScreencastFrame {
  timestampMs: number;
  pageId: string;
  /** Workspace-relative path to the `.jpg`. */
  file: string;
  keptReason: string;
}

export interface StepRollupInput {
  workspaceRoot: string;
  /** ISO. */
  runStartedAt: string | null;
  steps: ReadonlyArray<StepRollupStep>;
  thoughts: ReadonlyArray<StepRollupThought>;
  actions: ReadonlyArray<StepRollupAction>;
  navigation: ReadonlyArray<StepRollupNavigation>;
  network: ReadonlyArray<StepRollupNetwork>;
  consoleLogs: ReadonlyArray<StepRollupConsole>;
  assertions: ReadonlyArray<StepRollupAssertion>;
  /** Continuous screencast frames (real pixels), interleaved into each step's window. */
  screencastFrames?: ReadonlyArray<StepRollupScreencastFrame>;
}

// ============================================================================
// Builder
// ============================================================================

export const writeStepRollups = async (input: StepRollupInput): Promise<void> => {
  const baselineMs = input.runStartedAt ? Date.parse(input.runStartedAt) : null;
  const sortedSteps = input.steps.slice().sort((a, b) => a.orderIndex - b.orderIndex);

  // Pre-bucket time-stamped events for fast window assignment.
  const thoughtsByStepId = new Map<string, StepRollupThought[]>();
  for (const t of input.thoughts) {
    if (!t.stepId) continue;
    const list = thoughtsByStepId.get(t.stepId) ?? [];
    list.push(t);
    thoughtsByStepId.set(t.stepId, list);
  }

  const assertionsByStepId = new Map<string, StepRollupAssertion[]>();
  for (const a of input.assertions) {
    const list = assertionsByStepId.get(a.stepId) ?? [];
    list.push(a);
    assertionsByStepId.set(a.stepId, list);
  }

  // Step bounds for windowing untyped events (network, console, actions, nav).
  // For each step we use [startedAtMs, endMs) where endMs is finishedAtMs or
  // (next step's startedAtMs) — whichever fires first. This avoids gaps and
  // lets the agent attribute events that happened in the gap to the prior step.
  const bounds = computeStepBounds(sortedSteps);

  // True execution windows derived from step-started/step-completed annotations.
  // When present, these override the record bounds so a cache/playbook step whose
  // record timing is only the commit moment still surfaces the live work (clicks,
  // mutations) that ran under its playbook bracket. Steps without a matching
  // bracket fall back to `bounds` above.
  const workWindows = computeStepWorkWindows(sortedSteps, input.actions);

  // Build all rollup contents in-memory, then flush concurrently — distinct paths,
  // no contention.
  const writes: Array<{ relativePath: string; body: string }> = [];

  for (let i = 0; i < sortedSteps.length; i++) {
    const step = sortedSteps[i]!;
    const workWindow = workWindows.get(step.id);
    const window = workWindow ?? bounds[i]!;
    const lines: string[] = [];

    const startPrefix = formatPrefix(step.startedAtMs, baselineMs);
    const finishPrefix = formatPrefix(step.finishedAtMs, baselineMs);

    lines.push(`# Step ${step.orderIndex}: ${step.name ?? step.nodeId}`);
    lines.push('');
    lines.push(`- nodeId: \`${step.nodeId}\``);
    if (step.nodeType) lines.push(`- nodeType: \`${step.nodeType}\``);
    lines.push(`- status: **${step.status}**`);
    lines.push(`- started: ${startPrefix}`);
    lines.push(`- finished: ${finishPrefix}`);
    if (step.durationMs !== null) lines.push(`- duration: ${step.durationMs}ms`);
    // When the live playbook bracket runs wider than the recorded step row (a
    // cache/playbook step whose row is the commit moment), call it out so the
    // action timeline below — which spans the real work — isn't read as a
    // contradiction of the 254ms-style record duration.
    if (
      workWindow &&
      baselineMs !== null &&
      (step.startedAtMs === null ||
        workWindow.startMs < step.startedAtMs ||
        (step.finishedAtMs !== null && workWindow.endMs > step.finishedAtMs))
    ) {
      const a = formatRelative(workWindow.startMs - baselineMs);
      const b = formatRelative(workWindow.endMs - baselineMs);
      lines.push(
        `- work window: ${a} → ${b} (live playbook run; events below span this, not the record duration)`
      );
    }
    if (step.cacheOutcome) {
      const src = step.cacheHitSource ? `/${step.cacheHitSource}` : '';
      lines.push(`- cache: ${step.cacheOutcome}${src}`);
    }
    if (step.errorMessage) {
      lines.push('');
      lines.push('## Error message');
      lines.push('');
      lines.push('```');
      lines.push(step.errorMessage);
      lines.push('```');
    }
    if (step.diagnostics) {
      lines.push('');
      lines.push('## Runner diagnostics');
      lines.push('');
      const d = step.diagnostics;
      if (d.authDiagnostics) appendAuthDiagnostics(lines, d.authDiagnostics);
      if (d.failureType) lines.push(`- failure type: ${d.failureType}`);
      if (d.timedOut)
        lines.push(`- timed out${d.timeoutSeconds ? ` after ${d.timeoutSeconds}s` : ''}`);
      if (d.skippedReason) lines.push(`- skipped: ${oneLine(d.skippedReason)}`);
      if (d.conditionResult) lines.push(`- condition result: ${d.conditionResult}`);
      if (d.failureContext) {
        for (const key of ['observedBehavior', 'expectedBehavior', 'reproSteps'] as const) {
          const value = d.failureContext[key];
          if (typeof value === 'string') lines.push(`- ${key}: ${oneLine(value)}`);
        }
      }
      if (d.failureSnapshotYaml) {
        lines.push('', '### Failure snapshot', '', '```yaml', d.failureSnapshotYaml, '```');
      }
      if (d.apiSequence) {
        lines.push(
          `- API sequence: ${d.apiSequence.stepsExecuted ?? d.apiSequence.stepResults.length} calls, ${d.apiSequence.totalDurationMs ?? '?'}ms${d.apiSequence.failedStepId ? `; failed at ${d.apiSequence.failedStepId}` : ''}`
        );
        for (const result of d.apiSequence.stepResults) {
          lines.push(
            `  - ${result.success ? 'passed' : 'FAILED'} ${result.method ?? '?'} ${result.resolvedUrl ?? '?'} → ${result.statusCode ?? result.error ?? '?'}`
          );
          if (result.body) lines.push(`    response: ${oneLine(result.body)}`);
        }
      }
    }

    // Thoughts.
    const stepThoughts = (thoughtsByStepId.get(step.id) ?? [])
      .slice()
      .sort((a, b) => a.timestampMs - b.timestampMs);
    if (stepThoughts.length > 0) {
      lines.push('');
      lines.push('## Agent thoughts');
      lines.push('');
      for (const t of stepThoughts) {
        const tool = t.toolName ? ` [${t.toolName}]` : '';
        lines.push(
          `${formatPrefix(t.timestampMs, baselineMs)} thought${tool}: ${oneLine(t.content)}`
        );
      }
    }

    // Assertions.
    const stepAssertions = (assertionsByStepId.get(step.id) ?? []).slice();
    if (stepAssertions.length > 0) {
      lines.push('');
      lines.push('## Assertions');
      lines.push('');
      for (const a of stepAssertions) {
        const tag = a.passed ? 'passed' : 'FAILED';
        const severity = a.severity ? ` [${a.severity}${a.nonBlocking ? ' nonBlocking' : ''}]` : '';
        const cond = a.condition ?? '(no condition)';
        const reason = a.reason ? ` — ${oneLine(a.reason)}` : '';
        lines.push(`- ${tag}${severity}: ${oneLine(cond)}${reason}`);
      }
    }

    // Window-bound events.
    const inWindow = (ts: number | null): boolean => {
      if (ts === null) return false;
      if (window.startMs !== null && ts < window.startMs) return false;
      if (window.endMs !== null && ts >= window.endMs) return false;
      return true;
    };

    const windowActions = input.actions
      .filter((a) => inWindow(a.timestampMs))
      .slice()
      .sort(byTs);
    if (windowActions.length > 0) {
      lines.push('');
      lines.push('## Action timeline (in step window)');
      lines.push('');
      for (const a of windowActions) {
        const target = a.target ? ` ${oneLine(a.target)}` : '';
        const url = a.url ? ` → ${a.url}` : '';
        const fail = a.success ? '' : ' (failed)';
        lines.push(
          `${formatPrefix(a.timestampMs, baselineMs)} action.${a.action}:${target}${url}${fail}`
        );
      }
    }

    const windowNav = input.navigation
      .filter((n) => inWindow(n.timestampMs))
      .slice()
      .sort(byTs);
    if (windowNav.length > 0) {
      lines.push('');
      lines.push('## Navigation (in step window)');
      lines.push('');
      for (const n of windowNav) {
        const cross = n.crossHost ? ' ⚠ cross-host' : '';
        lines.push(
          `${formatPrefix(n.timestampMs, baselineMs)} nav.${n.action}: ${n.from ?? '?'} → ${n.to ?? '?'}${cross}`
        );
      }
    }

    const windowNet = input.network
      .filter((n) => inWindow(n.timestampMs))
      .slice()
      .sort(byTs);
    if (windowNet.length > 0) {
      lines.push('');
      lines.push('## Network (in step window)');
      lines.push('');
      for (const n of windowNet) {
        const status = n.status !== null ? ` ${n.status}` : '';
        lines.push(`${formatPrefix(n.timestampMs, baselineMs)} ${n.method} ${n.url}${status}`);
      }
    }

    const windowCon = input.consoleLogs
      .filter((c) => inWindow(c.timestampMs))
      .slice()
      .sort(byTs);
    if (windowCon.length > 0) {
      lines.push('');
      lines.push('## Console (in step window)');
      lines.push('');
      for (const c of windowCon) {
        lines.push(
          `${formatPrefix(c.timestampMs, baselineMs)} console.${c.type}: ${oneLine(c.text)}`
        );
      }
    }

    // Continuous screencast — real pixels covering EVERY moment in the window,
    // including evaluate/wait moments where no action-snapshot frame exists. Cap
    // the listing but always keep the boundary + any failure-window frames so the
    // agent can confirm a step's *effect*, not just that an action fired.
    const windowFrames = (input.screencastFrames ?? [])
      .filter((f) => inWindow(f.timestampMs))
      .slice()
      .sort(byTs);
    if (windowFrames.length > 0) {
      const shown = capScreencastFrames(windowFrames, SCREENCAST_ROLLUP_CAP);
      lines.push('');
      lines.push('## Screencast frames (in step window)');
      lines.push('');
      for (const f of shown) {
        const page = f.pageId ? ` [page ${f.pageId}]` : '';
        lines.push(
          `${formatPrefix(f.timestampMs, baselineMs)} \`${f.file}\` (${f.keptReason})${page}`
        );
      }
      if (windowFrames.length > shown.length) {
        lines.push(
          `- …${windowFrames.length - shown.length} more in \`${WORKSPACE_PATHS.browserScreencastIndex}\``
        );
      }
    }

    // Artifact references — paths to evidence files.
    lines.push('');
    lines.push('## Evidence files');
    lines.push('');
    lines.push(`- Thoughts: \`${thoughtsByStepRelativePath(step.id)}\``);
    lines.push(`- Workflow node: \`${workflowNodeRelativePath(step.nodeId)}\``);
    lines.push(
      `- Browser snapshots (if available): grep this step's timestamp in \`${WORKSPACE_PATHS.browserActions}\`, then \`browser/doms/...\` / \`browser/frames/...\` / \`browser/a11y/...\``
    );
    lines.push(
      `- Continuous screencast (real pixels; covers evaluate/wait moments with no action frame): \`${WORKSPACE_PATHS.browserScreencastIndex}\` — read the \`.jpg\` nearest the moment to confirm a step's effect.`
    );
    lines.push('');

    writes.push({
      relativePath: stepRollupRelativePath(step.orderIndex, step.id),
      body: lines.join('\n'),
    });
  }

  await Promise.all(
    writes.map(({ relativePath, body }) =>
      fs.writeFile(resolveWorkspacePath(input.workspaceRoot, relativePath), body, 'utf-8')
    )
  );
};

// ============================================================================
// Helpers
// ============================================================================

interface StepBounds {
  startMs: number | null;
  endMs: number | null;
}

const computeStepBounds = (sorted: ReadonlyArray<StepRollupStep>): StepBounds[] => {
  const bounds: StepBounds[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const step = sorted[i]!;
    const next = sorted[i + 1];
    const startMs = step.startedAtMs;
    let endMs: number | null = step.finishedAtMs;
    if (endMs === null && next?.startedAtMs !== undefined) endMs = next.startedAtMs;
    bounds.push({ startMs, endMs });
  }
  return bounds;
};

const byTs = <T extends { timestampMs: number | null }>(a: T, b: T): number => {
  if (a.timestampMs === null && b.timestampMs === null) return 0;
  if (a.timestampMs === null) return 1;
  if (b.timestampMs === null) return -1;
  return a.timestampMs - b.timestampMs;
};

/** Max screencast frames listed inline per step rollup (the rest stay in the index). */
const SCREENCAST_ROLLUP_CAP = 8;

/**
 * Cap the inline screencast listing while preserving signal: always keep the
 * first and last frame and any non-`cadence` frame (action/step-boundary/
 * failure-window — the diagnostically meaningful ones); fill the remaining
 * budget with evenly-spaced cadence frames. Input is assumed sorted by time.
 */
const capScreencastFrames = <T extends { keptReason: string }>(
  frames: ReadonlyArray<T>,
  cap: number
): T[] => {
  if (frames.length <= cap) return frames.slice();
  const meaningful: number[] = [0, frames.length - 1];
  frames.forEach((f, i) => {
    if (f.keptReason !== 'cadence') meaningful.push(i);
  });
  const keep = new Set<number>(meaningful);

  if (keep.size >= cap) {
    // Meaningful frames already fill the budget — evenly subsample them.
    const sorted = [...keep].sort((a, b) => a - b);
    const stride = (sorted.length - 1) / (cap - 1);
    const picked = new Set<number>();
    for (let k = 0; k < cap; k++) picked.add(sorted[Math.round(k * stride)]!);
    return [...picked].sort((a, b) => a - b).map((i) => frames[i]!);
  }

  // Backfill the remaining budget with cadence frames spread evenly across time.
  const cadenceIdx = frames.map((_, i) => i).filter((i) => !keep.has(i));
  const need = cap - keep.size;
  if (cadenceIdx.length > 0 && need > 0) {
    const stride = cadenceIdx.length / need;
    for (let k = 0; k < need; k++) keep.add(cadenceIdx[Math.floor(k * stride)]!);
  }
  return [...keep].sort((a, b) => a - b).map((i) => frames[i]!);
};
